// Intent: "Skarbiec rodziny" — the family's sensitive basics (PESELs, birth
// dates, anniversaries, document scans) kept ON DEVICE ONLY. HARD privacy rule:
// nothing here is ever included in the cloud-sync snapshot (see db/snapshot.ts,
// which deliberately does not know about the vault). PESELs go to the platform
// keychain (expo-secure-store) on native and are NOT collected on web; document
// files live in the app-private sandbox (FileSystem documentDirectory/vault).
// Legal grounding: a private photo of your OWN ID is not a "replika" (art. 58
// ustawy o dokumentach publicznych) and on-device-only data falls under the
// RODO household exemption — cloud would change that, hence local-only v1.

import { Platform } from 'react-native';
import { create } from 'zustand';

import { getDb } from '@/db';
import { uuid } from '@/utils/format';

const KV_VAULT = 'vault_meta';
const PESEL_KEY = (memberId: string) => `vault_pesel_${memberId}`;

export type VaultRelation = 'SELF' | 'PARTNER' | 'CHILD' | 'OTHER';
export type VaultDateKind = 'WEDDING' | 'MET' | 'CUSTOM';

export interface VaultMember {
  id: string;
  firstName: string;
  lastName: string | null;
  relation: VaultRelation;
  birthDate: string | null; // ISO yyyy-mm-dd
  hasPesel: boolean; // the PESEL itself lives in SecureStore, never in this blob
}

export interface VaultDate {
  id: string;
  kind: VaultDateKind;
  label: string | null; // for CUSTOM
  date: string; // ISO yyyy-mm-dd
}

export interface VaultDocument {
  id: string;
  label: string;
  fileUri: string;
  addedAt: string;
}

interface VaultBlob {
  members: VaultMember[];
  dates: VaultDate[];
  documents: VaultDocument[];
  remindersEnabled: boolean;
}

const DEFAULTS: VaultBlob = { members: [], dates: [], documents: [], remindersEnabled: false };

interface VaultState extends VaultBlob {
  hydrated: boolean;
  hydrate: () => Promise<void>;
  addMember: (input: {
    firstName: string;
    lastName?: string | null;
    relation: VaultRelation;
    birthDate?: string | null;
    pesel?: string | null;
  }) => Promise<void>;
  removeMember: (id: string) => Promise<void>;
  /** Reveal-on-tap: reads the PESEL from the keychain (native only). */
  getPesel: (memberId: string) => Promise<string | null>;
  setDate: (kind: VaultDateKind, date: string, label?: string | null) => Promise<void>;
  removeDate: (id: string) => Promise<void>;
  /** Copies the picked file into the app-private vault dir (native only). */
  addDocument: (label: string, sourceUri: string) => Promise<void>;
  removeDocument: (id: string) => Promise<void>;
  setRemindersEnabled: (enabled: boolean) => Promise<void>;
}

async function persist(blob: VaultBlob): Promise<void> {
  const db = await getDb();
  await db.setKv(KV_VAULT, JSON.stringify(blob));
}

function blobOf(s: VaultState, patch: Partial<VaultBlob>): VaultBlob {
  return {
    members: s.members,
    dates: s.dates,
    documents: s.documents,
    remindersEnabled: s.remindersEnabled,
    ...patch,
  };
}

/** App-private directory for document scans (never the camera roll). */
async function vaultDir() {
  const { Directory, Paths } = await import('expo-file-system');
  const dir = new Directory(Paths.document, 'vault');
  if (!dir.exists) dir.create();
  return dir;
}

export const useVaultStore = create<VaultState>((set, get) => ({
  hydrated: false,
  ...DEFAULTS,

  hydrate: async () => {
    const db = await getDb();
    const raw = await db.getKv(KV_VAULT);
    const blob: VaultBlob = raw ? { ...DEFAULTS, ...(JSON.parse(raw) as Partial<VaultBlob>) } : DEFAULTS;
    set({ hydrated: true, ...blob });
  },

  addMember: async ({ firstName, lastName = null, relation, birthDate = null, pesel = null }) => {
    const id = uuid();
    const storePesel = Platform.OS !== 'web' && !!pesel?.trim();
    if (storePesel) {
      const SecureStore = await import('expo-secure-store');
      await SecureStore.setItemAsync(PESEL_KEY(id), pesel!.trim());
    }
    const member: VaultMember = {
      id,
      firstName: firstName.trim(),
      lastName: lastName?.trim() || null,
      relation,
      birthDate,
      hasPesel: storePesel,
    };
    const next = blobOf(get(), { members: [...get().members, member] });
    await persist(next);
    set(next);
    await syncVaultReminders(next);
  },

  removeMember: async (id) => {
    if (Platform.OS !== 'web') {
      const SecureStore = await import('expo-secure-store');
      await SecureStore.deleteItemAsync(PESEL_KEY(id)).catch(() => undefined);
    }
    const next = blobOf(get(), { members: get().members.filter((m) => m.id !== id) });
    await persist(next);
    set(next);
    await syncVaultReminders(next);
  },

  getPesel: async (memberId) => {
    if (Platform.OS === 'web') return null;
    const SecureStore = await import('expo-secure-store');
    return SecureStore.getItemAsync(PESEL_KEY(memberId));
  },

  setDate: async (kind, date, label = null) => {
    const dates = get().dates;
    // WEDDING/MET are singletons — setting replaces; CUSTOM always appends.
    const kept = kind === 'CUSTOM' ? dates : dates.filter((d) => d.kind !== kind);
    const next = blobOf(get(), { dates: [...kept, { id: uuid(), kind, label, date }] });
    await persist(next);
    set(next);
    await syncVaultReminders(next);
  },

  removeDate: async (id) => {
    const next = blobOf(get(), { dates: get().dates.filter((d) => d.id !== id) });
    await persist(next);
    set(next);
    await syncVaultReminders(next);
  },

  addDocument: async (label, sourceUri) => {
    if (Platform.OS === 'web') return; // documents are native-only
    const { File } = await import('expo-file-system');
    const dir = await vaultDir();
    const id = uuid();
    const ext = sourceUri.includes('.') ? sourceUri.slice(sourceUri.lastIndexOf('.')) : '.jpg';
    const src = new File(sourceUri);
    const dest = new File(dir, `${id}${ext}`);
    src.copy(dest);
    const doc: VaultDocument = { id, label, fileUri: dest.uri, addedAt: new Date().toISOString() };
    const next = blobOf(get(), { documents: [...get().documents, doc] });
    await persist(next);
    set(next);
  },

  removeDocument: async (id) => {
    const doc = get().documents.find((d) => d.id === id);
    if (doc && Platform.OS !== 'web') {
      const { File } = await import('expo-file-system');
      try {
        new File(doc.fileUri).delete();
      } catch {
        // metadata cleanup still proceeds
      }
    }
    const next = blobOf(get(), { documents: get().documents.filter((d) => d.id !== id) });
    await persist(next);
    set(next);
  },

  setRemindersEnabled: async (enabled) => {
    const next = blobOf(get(), { remindersEnabled: enabled });
    await persist(next);
    set(next);
    await syncVaultReminders(next);
  },
}));

/**
 * Yearly local notifications for birthdays and anniversaries (identifiers
 * vault-*, exempt from the reminder sync). Full reschedule — idempotent.
 */
async function syncVaultReminders(blob: VaultBlob): Promise<void> {
  if (Platform.OS === 'web') return;
  try {
    const Notifications = await import('expo-notifications');
    const scheduled = await Notifications.getAllScheduledNotificationsAsync();
    for (const n of scheduled) {
      if (n.identifier.startsWith('vault-')) {
        await Notifications.cancelScheduledNotificationAsync(n.identifier);
      }
    }
    if (!blob.remindersEnabled) return;

    const entries: { id: string; title: string; date: string }[] = [
      ...blob.members
        .filter((m) => m.birthDate)
        .map((m) => ({ id: `vault-bday-${m.id}`, title: `Urodziny: ${m.firstName}`, date: m.birthDate! })),
      ...blob.dates.map((d) => ({
        id: `vault-date-${d.id}`,
        title:
          d.kind === 'WEDDING' ? 'Rocznica ślubu' : d.kind === 'MET' ? 'Rocznica poznania' : d.label ?? 'Ważna data',
        date: d.date,
      })),
    ];
    for (const e of entries) {
      const [, m, day] = e.date.split('-').map(Number);
      if (!m || !day) continue;
      await Notifications.scheduleNotificationAsync({
        identifier: e.id,
        content: { title: e.title, body: 'Dziś ważny dzień dla Waszej rodziny.' },
        trigger: {
          type: Notifications.SchedulableTriggerInputTypes.YEARLY,
          month: m,
          day,
          hour: 9,
          minute: 0,
        },
      });
    }
  } catch {
    // Notifications are best-effort; the vault data itself is already saved.
  }
}
